<script>
import CustomerLayout from '@/Layouts/CustomerLayout.vue';

export default {
  layout: CustomerLayout,
};
</script>

<script setup>
import { computed, nextTick, ref } from 'vue';
import { usePage, Link, Head, router } from '@inertiajs/vue3';
import EmptyState from '@/Components/EmptyState.vue';
import TransactionHistoryList from '@/Components/Customer/TransactionHistoryList.vue';
import PulsaFlow from '@/Components/Customer/PulsaFlow.vue';
import DataPackageFlow from '@/Components/Customer/DataPackageFlow.vue';
import EWalletFlow from '@/Components/Customer/EWalletFlow.vue';
import PlnPostpaidFlow from '@/Components/Customer/PlnPostpaidFlow.vue';
import { Smartphone, Wifi, Zap, WalletCards, Gamepad2, Receipt, ArrowRight, UserCheck, Wallet, Info, CheckCircle2, AlertTriangle, RefreshCw, Search, Check, Copy, X, ShieldAlert, Clock, FileText } from '@lucide/vue';
import axios from 'axios';

const page = usePage();
const auth = computed(() => page.props.auth);
const user = computed(() => auth.value?.user);
const isGuest = computed(() => auth.value?.is_guest);
const catalogue = computed(() => page.props.catalogue || []);
const operators = computed(() => page.props.operators || []);
const transactions = computed(() => page.props.transactions || []);
const ppobConfig = computed(() => page.props.ppob_config || {});
const wallet = computed(() => page.props.wallet || auth.value?.user?.wallet || { balance_minor: 0 });

const topupAmounts = [50000, 100000, 250000, 500000, 1000000];
const topupModalOpen = ref(false);
const topupStep = ref('amount');
const selectedTopupAmount = ref(null);
const customTopupAmount = ref('');
const topupError = ref('');
const topupSubmitting = ref(false);
const topupResult = ref(null);
const topupIdempotencyKey = ref('');

const topupAmount = computed(() => selectedTopupAmount.value === 'custom'
  ? Number(customTopupAmount.value || 0)
  : Number(selectedTopupAmount.value || 0));
const topupAmountValid = computed(() => Number.isInteger(topupAmount.value) && topupAmount.value >= 10000);

function formatTopupDigits(digits) {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

function caretAfterDigitCount(formattedValue, digitCount) {
  if (digitCount <= 0) return 0;

  let digitsSeen = 0;
  for (let index = 0; index < formattedValue.length; index += 1) {
    if (/\d/.test(formattedValue[index])) digitsSeen += 1;
    if (digitsSeen === digitCount) return index + 1;
  }

  return formattedValue.length;
}

function setCustomTopupValue(input, digits, caretDigitCount) {
  const formattedValue = formatTopupDigits(digits);
  customTopupAmount.value = digits;
  input.value = formattedValue;

  nextTick(() => {
    input.value = formattedValue;
    const caret = caretAfterDigitCount(formattedValue, caretDigitCount);
    input.setSelectionRange(caret, caret);
  });
}

function onCustomTopupInput(event) {
  const input = event.target;
  const caret = input.selectionStart ?? input.value.length;
  const digitCountBeforeCaret = (input.value.slice(0, caret).match(/\d/g) || []).length;
  const digits = input.value.replace(/\D/g, '');

  setCustomTopupValue(input, digits, digitCountBeforeCaret);
}

function onCustomTopupBeforeInput(event) {
  const input = event.target;
  if (input.selectionStart !== input.selectionEnd) return;

  const caret = input.selectionStart ?? 0;
  const isBackwardAcrossSeparator = event.inputType === 'deleteContentBackward' && input.value[caret - 1] === '.';
  const isForwardAcrossSeparator = event.inputType === 'deleteContentForward' && input.value[caret] === '.';
  if (!isBackwardAcrossSeparator && !isForwardAcrossSeparator) return;

  event.preventDefault();
  const digits = input.value.replace(/\D/g, '');
  const digitCountBeforeCaret = (input.value.slice(0, caret).match(/\d/g) || []).length;
  const deletionIndex = isBackwardAcrossSeparator ? digitCountBeforeCaret - 1 : digitCountBeforeCaret;
  const updatedDigits = digits.slice(0, deletionIndex) + digits.slice(deletionIndex + 1);
  const updatedCaretDigitCount = isBackwardAcrossSeparator ? deletionIndex : digitCountBeforeCaret;

  setCustomTopupValue(input, updatedDigits, updatedCaretDigitCount);
}

function openTopup() {
  selectedTopupAmount.value = null;
  customTopupAmount.value = '';
  topupError.value = '';
  topupResult.value = null;
  topupStep.value = 'amount';
  topupModalOpen.value = true;
}

function reviewTopup() {
  topupError.value = '';
  if (!topupAmountValid.value) {
    topupError.value = 'Nominal minimal top-up adalah Rp 10.000.';
    return;
  }
  topupIdempotencyKey.value = crypto.randomUUID();
  topupStep.value = 'confirm';
}

async function confirmTopup() {
  if (topupSubmitting.value || !topupAmountValid.value) return;
  topupSubmitting.value = true;
  topupError.value = '';
  try {
    const res = await axios.post(route('wallet.mock-topups.store'), {
      amount_minor: topupAmount.value,
      payment_method: 'simulation',
      idempotency_key: topupIdempotencyKey.value,
    });
    topupResult.value = res.data;
    topupStep.value = 'success';
    await router.reload({ only: ['wallet', 'transactions'] });
  } catch (error) {
    topupError.value = error.response?.data?.message || error.response?.data?.errors?.amount_minor?.[0] || 'Top-up gagal diproses.';
  } finally {
    topupSubmitting.value = false;
  }
}

const selectedCategoryCode = ref('pulsa');
const plnSubTab = ref('prepaid'); // 'prepaid' (Token PLN) or 'postpaid' (PLN Pascabayar)

const activeCategory = computed(() => {
  return catalogue.value.find(c => c.code === selectedCategoryCode.value) || catalogue.value[0];
});

const isEWalletCategory = computed(() => {
  return selectedCategoryCode.value === 'ewallet';
});

const isPlnCategory = computed(() => {
  return selectedCategoryCode.value === 'pln';
});

const isTagihanCategory = computed(() => {
  return selectedCategoryCode.value === 'tagihan';
});

// ==========================================
// MOCK TOKEN PLN STATE MACHINE
// ==========================================
const tokenPlnMeterNumber = ref('');
const tokenPlnInquiry = ref(null);
const tokenPlnSelectedSku = ref(null);
const tokenPlnIsChecking = ref(false);
const tokenPlnIsSubmitting = ref(false);
const tokenPlnError = ref('');
const tokenPlnReviewModalOpen = ref(false);
const tokenPlnResultModalOpen = ref(false);
const tokenPlnPurchaseResult = ref(null);
const tokenPlnIdempotencyKey = ref('');
const copiedToken = ref(false);

const normalizedPlnMeterNumber = computed(() => {
  return tokenPlnMeterNumber.value.replace(/[\s-]/g, '').replace(/\D/g, '');
});

const isPlnMeterNumberValid = computed(() => {
  const norm = normalizedPlnMeterNumber.value;
  return norm.length >= 9 && norm.length <= 16;
});

function onPlnMeterNumberInput(e) {
  const raw = e.target.value;
  let normalized = raw.replace(/[\s-]/g, '').replace(/\D/g, '');
  if (normalized.length > 16) {
    normalized = normalized.substring(0, 16);
  }
  e.target.value = normalized;
  tokenPlnMeterNumber.value = normalized;

  tokenPlnInquiry.value = null;
  tokenPlnSelectedSku.value = null;
  tokenPlnError.value = '';
  tokenPlnPurchaseResult.value = null;
}

async function runTokenPlnInquiry() {
  if (!isPlnMeterNumberValid.value || tokenPlnIsChecking.value) return;

  tokenPlnIsChecking.value = true;
  tokenPlnError.value = '';
  tokenPlnInquiry.value = null;
  tokenPlnSelectedSku.value = null;

  try {
    const res = await axios.post(route('token-pln.inquiries.store'), {
      customer_number: normalizedPlnMeterNumber.value,
    });

    if (res.data && res.data.success) {
      tokenPlnInquiry.value = res.data;
    } else {
      tokenPlnError.value = res.data.message || 'Pengecekan nomor meter tidak berhasil.';
    }
  } catch (e) {
    tokenPlnError.value = e.response?.data?.message || e.response?.data?.error_message || 'Gagal melakukan pengecekan nomor meter.';
  } finally {
    tokenPlnIsChecking.value = false;
  }
}

function openPlnReviewModal() {
  if (!tokenPlnInquiry.value || !tokenPlnSelectedSku.value) return;
  tokenPlnIdempotencyKey.value = crypto.randomUUID();
  tokenPlnReviewModalOpen.value = true;
}

async function confirmTokenPlnPurchase() {
  if (!tokenPlnInquiry.value || !tokenPlnSelectedSku.value || tokenPlnIsSubmitting.value) return;

  tokenPlnIsSubmitting.value = true;
  tokenPlnError.value = '';

  try {
    const res = await axios.post(route('token-pln.mock-transactions.store'), {
      inquiry_reference: tokenPlnInquiry.value.inquiry_reference,
      master_sku_id: tokenPlnSelectedSku.value.id,
      idempotency_key: tokenPlnIdempotencyKey.value,
    });

    if (res.data && res.data.success) {
      tokenPlnPurchaseResult.value = res.data;
      tokenPlnReviewModalOpen.value = false;
      tokenPlnResultModalOpen.value = true;

      router.reload({ only: ['transactions'] });
    } else {
      tokenPlnError.value = res.data.message || 'Simulasi transaksi gagal.';
    }
  } catch (e) {
    tokenPlnError.value = e.response?.data?.message || 'Terjadi kesalahan saat memproses transaksi simulasi.';
  } finally {
    tokenPlnIsSubmitting.value = false;
  }
}

function copyPlnToken(token) {
  if (!token) return;
  navigator.clipboard.writeText(token);
  copiedToken.value = true;
  setTimeout(() => {
    copiedToken.value = false;
  }, 2000);
}

function resetTokenPlnFlow() {
  tokenPlnMeterNumber.value = '';
  tokenPlnInquiry.value = null;
  tokenPlnSelectedSku.value = null;
  tokenPlnError.value = '';
  tokenPlnPurchaseResult.value = null;
  tokenPlnReviewModalOpen.value = false;
  tokenPlnResultModalOpen.value = false;
}

// Transaction Detail Modal
const selectedDetailTx = ref(null);

function viewTransactionDetail(tx) {
  selectedDetailTx.value = tx;
}

function formatRupiah(amountMinor) {
  if (amountMinor === null || amountMinor === undefined) return 'Rp 0';
  return 'Rp ' + Math.floor(amountMinor).toLocaleString('id-ID');
}

function formatTxTitle(tx) {
  if (!tx) return 'Produk PPOB';
  if (tx.kind === 'wallet' || tx.kind === 'topup_request') return 'Tambah Saldo';
  if (tx.sku?.product?.code === 'PLN_POSTPAID') {
    return 'PLN Pascabayar';
  }
  if (tx.sku?.code?.endsWith('_OPEN_AMOUNT')) {
    return (tx.sku?.product?.name || 'E-Wallet') + ' Nominal Bebas';
  }
  return tx.sku?.name || tx.sku?.product?.name || 'Produk PPOB';
}

function topupStatusLabel(status) {
  return { pending: 'Menunggu Persetujuan', approved: 'Disetujui', rejected: 'Ditolak', success: 'Success' }[status] || status;
}

function topupStatusClass(status) {
  return {
    pending: 'text-amber-700 bg-amber-50 border-amber-100',
    approved: 'text-emerald-600 bg-emerald-50 border-emerald-100',
    rejected: 'text-rose-600 bg-rose-50 border-rose-100',
    success: 'text-emerald-600 bg-emerald-50 border-emerald-100',
  }[status] || 'text-slate-600 bg-slate-50 border-slate-100';
}

function maskIdentifier(id) {
  if (!id) return '';
  if (id.length <= 6) return id;
  return id.substring(0, 4) + '****' + id.substring(id.length - 4);
}

function getCategoryIcon(iconName) {
  switch (iconName) {
    case 'smartphone': return Smartphone;
    case 'wifi': return Wifi;
    case 'zap': return Zap;
    case 'wallet': return WalletCards;
    case 'gamepad-2': return Gamepad2;
    case 'file-text': return Receipt;
    default: return Receipt;
  }
}
</script>

<template>
  <Head title="Katalog Produk PPOB" />

  <div class="space-y-8 pb-12">
    <!-- Top Welcome Banner -->
    <div class="relative overflow-hidden rounded-3xl bg-slate-900 text-white p-6 sm:p-8 shadow-xl border border-slate-800">
      <div class="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div class="space-y-2 max-w-xl">
          <div class="flex items-center gap-2">
            <span v-if="isGuest" class="px-2.5 py-1 rounded-full bg-amber-400 text-slate-950 font-extrabold text-[10px] uppercase tracking-wider">
              MODE TAMU
            </span>
            <span v-else class="px-2.5 py-1 rounded-full bg-emerald-500 text-white font-extrabold text-[10px] uppercase tracking-wider">
              AKUN TERDAFTAR
            </span>
            <span class="text-xs text-slate-400 font-mono">Manza PPOB Platform</span>
          </div>

          <h1 class="text-xl sm:text-2xl font-black tracking-tight text-white">
            Selamat Datang, <span class="text-amber-300">{{ user?.name || 'Tamu PPOB' }}</span>
          </h1>

          <p class="text-xs text-slate-300 leading-relaxed">
            Katalog produk PPOB resmi terintegrasi dengan database master. Pilih kategori dan lakukan simulasi transaksi digital.
          </p>
        </div>

        <!-- Wallet Card Summary -->
        <div class="p-5 rounded-2xl bg-white/10 backdrop-blur-md border border-white/10 text-white w-full md:w-72 shrink-0 space-y-3">
          <div class="flex items-center justify-between text-xs text-slate-300">
            <span class="flex items-center gap-1.5 font-bold">
              <Wallet class="w-4 h-4 text-amber-300" />
              Saldo Dompet
            </span>
            <span class="text-[10px] px-2 py-0.5 rounded-md bg-white/20 font-bold uppercase">IDR</span>
          </div>

          <div class="text-2xl font-black text-amber-300 tracking-tight">
            {{ isGuest ? 'Rp 0' : formatRupiah(wallet.balance_minor) }}
          </div>

          <p class="text-[10px] text-slate-400 leading-tight">
            {{ isGuest ? 'Mode Tamu tidak memiliki dompet aktif.' : 'Saldo utama akun terdaftar.' }}
          </p>
          <button
            v-if="!isGuest"
            type="button"
            @click="openTopup"
            class="w-full py-2 rounded-xl bg-amber-300 hover:bg-amber-200 text-slate-950 text-xs font-black transition"
          >
            + Tambah Saldo
          </button>
        </div>
      </div>
    </div>

    <!-- PPOB Mock Mode Marker Banner -->
    <div v-if="ppobConfig.mock_mode" class="p-4 rounded-2xl bg-amber-50 border border-amber-200/90 text-amber-900 text-xs flex items-start gap-3">
      <Info class="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
      <div class="space-y-1">
        <div class="flex items-center gap-2">
          <span class="font-bold">MODE SIMULASI PPOB AKTIF</span>
          <span class="px-2 py-0.5 rounded-full bg-amber-200/80 text-amber-900 font-extrabold text-[10px] uppercase">MOCK</span>
        </div>
        <p class="text-[11px] text-amber-800/90 leading-relaxed">
          Sistem berjalan dalam mode simulasi lokal. Transaksi produk PPOB menggunakan provider simulasi backend. Saldo dompet tidak dipotong.
        </p>
      </div>
    </div>

    <!-- Category Selector Tabs -->
    <section class="space-y-4">
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-lg font-bold text-slate-900">Kategori Produk</h2>
          <p class="text-xs text-slate-500 mt-0.5">Pilih kategori produk digital untuk melihat varian nominal & paket</p>
        </div>
      </div>

      <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <button
          v-for="cat in catalogue"
          :key="cat.id"
          @click="selectedCategoryCode = cat.code"
          type="button"
          class="p-4 rounded-2xl border text-left transition flex flex-col justify-between space-y-2 shadow-2xs cursor-pointer"
          :class="selectedCategoryCode === cat.code ? 'border-indigo-600 bg-indigo-50/80 text-indigo-900 ring-2 ring-indigo-500/20' : 'border-slate-200 bg-white hover:border-slate-300 text-slate-800'"
        >
          <div class="w-8 h-8 rounded-xl flex items-center justify-center font-bold" :class="selectedCategoryCode === cat.code ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600'">
            <component :is="getCategoryIcon(cat.icon)" class="w-4 h-4" />
          </div>
          <div>
            <h4 class="font-bold text-xs truncate">{{ cat.name }}</h4>
            <span class="text-[10px] text-slate-500 font-medium block mt-0.5">{{ cat.products.length }} Produk</span>
          </div>
        </button>
      </div>
    </section>

    <!-- Guided Purchase Experience Container -->
    <section v-if="activeCategory" class="space-y-6">
      <div class="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm space-y-6">

        <div class="border-b border-slate-100 pb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 class="text-base font-bold text-slate-900">Form Transaksi {{ activeCategory.name }}</h3>
            <p class="text-xs text-slate-500 mt-0.5">{{ activeCategory.description }}</p>
          </div>

          <!-- PLN Sub-Tab Selector -->
          <div v-if="isPlnCategory" class="flex p-1 bg-slate-100 rounded-xl shrink-0">
            <button
              @click="plnSubTab = 'prepaid'"
              type="button"
              class="px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer"
              :class="plnSubTab === 'prepaid' ? 'bg-white text-indigo-900 shadow-xs' : 'text-slate-600 hover:text-slate-900'"
            >
              Token Prabayar
            </button>
            <button
              @click="plnSubTab = 'postpaid'"
              type="button"
              class="px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer"
              :class="plnSubTab === 'postpaid' ? 'bg-white text-indigo-900 shadow-xs' : 'text-slate-600 hover:text-slate-900'"
            >
              PLN Pascabayar
            </button>
          </div>
        </div>

        <!-- A. PULSA FLOW -->
        <PulsaFlow
          v-if="selectedCategoryCode === 'pulsa'"
          :category="activeCategory"
          :operators="operators"
          :ppob-config="ppobConfig"
          :format-rupiah="formatRupiah"
        />

        <!-- B. PAKET DATA FLOW -->
        <DataPackageFlow
          v-else-if="selectedCategoryCode === 'data'"
          :category="activeCategory"
          :operators="operators"
          :ppob-config="ppobConfig"
          :format-rupiah="formatRupiah"
        />

        <!-- C. E-WALLET FLOW -->
        <EWalletFlow
          v-else-if="isEWalletCategory"
          :category="activeCategory"
          :ppob-config="ppobConfig"
          :format-rupiah="formatRupiah"
        />

        <!-- D. PLN PASCABAYAR FLOW (when in Tagihan or PLN Postpaid subtab) -->
        <PlnPostpaidFlow
          v-else-if="isTagihanCategory || (isPlnCategory && plnSubTab === 'postpaid')"
          :category="activeCategory"
          :ppob-config="ppobConfig"
          :format-rupiah="formatRupiah"
        />

        <!-- E. TOKEN PLN GUIDED MOCK FLOW -->
        <div v-else-if="isPlnCategory && plnSubTab === 'prepaid'" class="space-y-6">
          <div v-for="product in activeCategory.products.filter(p => p.code === 'PLN_TOKEN' || p.transaction_type === 'voucher_token')" :key="product.id" class="space-y-6">

            <!-- Meter / Customer ID Input Row -->
            <div class="p-6 rounded-3xl bg-slate-50 border border-slate-200 space-y-4">
              <div class="flex items-center justify-between">
                <span class="text-[10px] font-bold uppercase tracking-wider text-slate-500">Cek ID Pelanggan PLN</span>
                <span v-if="ppobConfig.mock_mode" class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 uppercase">Simulasi Backend</span>
              </div>

              <form @submit.prevent="runTokenPlnInquiry" class="space-y-3">
                <label for="pln_meter_input" class="block text-xs font-bold text-slate-700">
                  Nomor Meter / ID Pelanggan <span class="text-rose-500">*</span>
                </label>

                <div class="flex flex-col sm:flex-row gap-3">
                  <div class="relative flex-1">
                    <input
                      id="pln_meter_input"
                      :value="tokenPlnMeterNumber"
                      @input="onPlnMeterNumberInput"
                      type="text"
                      inputmode="numeric"
                      placeholder="Masukkan 9–16 digit nomor meter"
                      class="w-full px-4 py-3 min-h-[44px] rounded-2xl border border-slate-300 text-sm font-semibold tracking-wide bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-500/20 transition outline-none"
                    />
                    <div v-if="tokenPlnIsChecking" class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                      <RefreshCw class="w-4 h-4 animate-spin" />
                    </div>
                  </div>

                  <button
                    :disabled="!isPlnMeterNumberValid || tokenPlnIsChecking"
                    type="submit"
                    class="px-6 py-3 min-h-[44px] rounded-2xl text-xs font-bold transition flex items-center justify-center gap-2 shadow-xs shrink-0 w-full sm:w-auto cursor-pointer"
                    :class="isPlnMeterNumberValid && !tokenPlnIsChecking
                      ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                      : 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none'"
                  >
                    <RefreshCw v-if="tokenPlnIsChecking" class="w-4 h-4 animate-spin" />
                    <Search v-else class="w-4 h-4" />
                    <span>{{ tokenPlnIsChecking ? 'Mengecek...' : 'Cek Nomor' }}</span>
                  </button>
                </div>

                <p class="text-[11px] text-slate-500">
                  Masukkan 9–16 digit nomor meter atau ID pelanggan untuk mengecek data listrik.
                </p>

                <!-- Customer Inquiry Card -->
                <div v-if="tokenPlnInquiry" class="p-4 rounded-2xl bg-indigo-50 border border-indigo-200 text-indigo-900 space-y-3">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                      <CheckCircle2 class="w-5 h-5 text-indigo-600 shrink-0" />
                      <span class="font-bold text-xs">Pelanggan PLN Ditemukan</span>
                    </div>
                    <span class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-200/80 text-amber-900 uppercase">
                      SIMULASI LOKAL
                    </span>
                  </div>

                  <div class="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs bg-white/80 p-3 rounded-xl border border-indigo-100 font-medium">
                    <div>
                      <span class="text-[10px] text-slate-500 block">No. Meter / ID</span>
                      <span class="font-mono font-bold text-slate-900">{{ tokenPlnInquiry.customer_number }}</span>
                    </div>
                    <div>
                      <span class="text-[10px] text-slate-500 block">Nama Pelanggan</span>
                      <span class="font-bold text-slate-900">{{ tokenPlnInquiry.customer_name }}</span>
                    </div>
                    <div>
                      <span class="text-[10px] text-slate-500 block">Tarif / Daya</span>
                      <span class="font-bold text-slate-900">{{ tokenPlnInquiry.tariff }} / {{ tokenPlnInquiry.power_va }} VA</span>
                    </div>
                  </div>

                  <p class="text-[11px] text-indigo-800/90 italic">
                    * Data di atas merupakan hasil pengecekan simulasi backend.
                  </p>
                </div>
              </form>
            </div>

            <!-- Nominal Stroom Token SKUs Grid -->
            <div class="space-y-4">
              <h4 class="font-bold text-xs text-slate-900">Pilih Nominal Token PLN</h4>

              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <div
                  v-for="sku in product.skus"
                  :key="sku.id"
                  @click="tokenPlnInquiry ? tokenPlnSelectedSku = sku : null"
                  class="p-4 rounded-2xl border transition flex flex-col justify-between space-y-3 relative"
                  :class="[
                    !tokenPlnInquiry ? 'opacity-60 cursor-not-allowed bg-slate-50 border-slate-200' : 'cursor-pointer',
                    tokenPlnSelectedSku?.id === sku.id ? 'border-indigo-600 bg-indigo-50/90 ring-2 ring-indigo-500/20' : 'border-slate-200 bg-white hover:border-slate-300'
                  ]"
                >
                  <div>
                    <div class="flex items-center justify-between">
                      <span class="font-mono text-[10px] font-bold text-slate-400">{{ sku.code }}</span>
                      <span class="text-[9px] font-semibold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">Stroom</span>
                    </div>
                    <h5 class="font-bold text-xs text-slate-900 mt-1">{{ sku.name }}</h5>
                    <p class="text-xs font-black text-indigo-600 mt-2">{{ formatRupiah(sku.fallback_selling_price_minor) }}</p>
                  </div>

                  <button
                    :disabled="!tokenPlnInquiry"
                    type="button"
                    class="w-full py-2 px-3 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1"
                    :class="tokenPlnSelectedSku?.id === sku.id
                      ? 'bg-indigo-600 text-white'
                      : (tokenPlnInquiry ? 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200' : 'bg-slate-200 text-slate-400 cursor-not-allowed')"
                  >
                    <Check v-if="tokenPlnSelectedSku?.id === sku.id" class="w-3.5 h-3.5" />
                    <span>{{ tokenPlnSelectedSku?.id === sku.id ? 'Terpilih' : 'Pilih Nominal' }}</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Error Alert -->
            <div v-if="tokenPlnError" class="p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-900 text-xs flex items-center gap-2">
              <AlertTriangle class="w-4 h-4 text-rose-600 shrink-0" />
              <span>{{ tokenPlnError }}</span>
            </div>

            <!-- Action Trigger for Review Modal -->
            <div v-if="tokenPlnInquiry && tokenPlnSelectedSku" class="p-5 rounded-2xl bg-indigo-900 text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-lg">
              <div>
                <span class="text-[10px] font-bold uppercase tracking-wider text-indigo-300 block">Produk Terpilih</span>
                <span class="text-lg font-black text-amber-300">{{ tokenPlnSelectedSku.name }}</span>
                <p class="text-xs text-indigo-200 mt-0.5">Harga: {{ formatRupiah(tokenPlnSelectedSku.fallback_selling_price_minor) }}</p>
              </div>

              <button
                @click="openPlnReviewModal"
                type="button"
                class="w-full sm:w-auto px-6 py-3 rounded-xl bg-amber-400 hover:bg-amber-300 text-slate-950 font-black text-xs transition shadow-md flex items-center justify-center gap-2 cursor-pointer"
              >
                <span>Lanjutkan Simulasi Pembelian</span>
                <ArrowRight class="w-4 h-4" />
              </button>
            </div>

          </div>
        </div>

        <!-- F. GENERIC CATALOGUE PRODUCT FALLBACK (e.g. Game Voucher) -->
        <div v-else class="space-y-6">
          <div v-for="product in activeCategory.products" :key="product.id" class="p-6 rounded-3xl bg-slate-50 border border-slate-200 space-y-4">
            <div class="flex items-center justify-between border-b border-slate-200 pb-3">
              <div>
                <h4 class="font-bold text-sm text-slate-900">{{ product.name }}</h4>
                <p class="text-xs text-slate-500 mt-0.5">{{ product.description }}</p>
              </div>
              <span class="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full border border-indigo-100 uppercase">{{ product.transaction_type }}</span>
            </div>

            <!-- Master SKUs Grid -->
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div
                v-for="sku in product.skus"
                :key="sku.id"
                class="p-4 rounded-2xl border border-slate-200 bg-slate-50/50 flex flex-col justify-between gap-3 hover:border-slate-300 transition"
              >
                <div>
                  <div class="flex items-center justify-between">
                    <span class="font-mono text-[10px] font-bold text-slate-400">{{ sku.code }}</span>
                    <span class="text-[9px] font-semibold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">Resmi</span>
                  </div>
                  <h5 class="font-bold text-xs text-slate-900 mt-1">{{ sku.name }}</h5>
                  <p class="text-xs font-black text-indigo-600 mt-2">{{ formatRupiah(sku.fallback_selling_price_minor) }}</p>
                </div>

                <button
                  disabled
                  type="button"
                  class="w-full py-2 px-3 rounded-xl bg-slate-200 text-slate-500 text-xs font-bold cursor-not-allowed"
                >
                  Belum Tersedia
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>

    <!-- Top-Up Modal -->
    <div v-if="topupModalOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
      <div class="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full space-y-5 shadow-2xl border border-slate-100">
        <div class="flex items-center justify-between border-b border-slate-100 pb-4">
          <div>
            <h3 class="font-black text-slate-900">Tambah Saldo</h3>
            <span class="text-[10px] font-extrabold text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full uppercase">Mode Simulasi</span>
          </div>
          <button :disabled="topupSubmitting" @click="topupModalOpen = false" class="text-slate-400 hover:text-slate-600"><X class="w-5 h-5" /></button>
        </div>

        <div v-if="topupStep === 'amount'" class="space-y-4">
          <p class="text-xs text-slate-600">Pilih nominal saldo yang ingin ditambahkan.</p>
          <div class="grid grid-cols-2 gap-2">
            <button v-for="amount in topupAmounts" :key="amount" type="button" @click="selectedTopupAmount = amount" :class="['py-3 rounded-xl border text-xs font-bold transition', selectedTopupAmount === amount ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-700']">
              {{ formatRupiah(amount) }}
            </button>
            <button type="button" @click="selectedTopupAmount = 'custom'" :class="['py-3 rounded-xl border text-xs font-bold transition', selectedTopupAmount === 'custom' ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-700']">Nominal Lainnya</button>
          </div>
          <input
            v-if="selectedTopupAmount === 'custom'"
            :value="formatTopupDigits(customTopupAmount)"
            type="text"
            inputmode="numeric"
            autocomplete="off"
            placeholder="Minimal Rp 10.000"
            class="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:border-indigo-500 outline-none"
            @beforeinput="onCustomTopupBeforeInput"
            @input="onCustomTopupInput"
          />
          <div class="p-3 rounded-xl bg-amber-50 border border-amber-200 text-[11px] text-amber-900">Top-up ini menggunakan pembayaran simulasi. Tidak ada transaksi uang sebenarnya.</div>
          <p v-if="topupError" class="text-xs text-red-600">{{ topupError }}</p>
          <button type="button" :disabled="!topupAmountValid" @click="reviewTopup" class="w-full py-3 rounded-xl bg-indigo-600 disabled:bg-slate-300 text-white text-xs font-bold">Lanjutkan</button>
        </div>

        <div v-else-if="topupStep === 'confirm'" class="space-y-4">
          <div class="space-y-2 p-4 rounded-2xl bg-slate-50 border border-slate-200 text-xs">
            <div class="flex justify-between"><span class="text-slate-500">Saldo Saat Ini</span><strong>{{ formatRupiah(wallet.balance_minor) }}</strong></div>
            <div class="flex justify-between"><span class="text-slate-500">Tambah Saldo</span><strong class="text-indigo-600">{{ formatRupiah(topupAmount) }}</strong></div>
            <div class="flex justify-between border-t pt-2"><span class="text-slate-500">Saldo Setelah Top-Up</span><strong>{{ formatRupiah(wallet.balance_minor + topupAmount) }}</strong></div>
            <div class="flex justify-between"><span class="text-slate-500">Pembayaran</span><strong>Simulasi</strong></div>
          </div>
          <p v-if="topupError" class="text-xs text-red-600">{{ topupError }}</p>
          <div class="flex gap-3">
            <button type="button" :disabled="topupSubmitting" @click="topupStep = 'amount'" class="flex-1 py-3 rounded-xl border border-slate-300 text-xs font-bold">Batal</button>
            <button type="button" :disabled="topupSubmitting" @click="confirmTopup" class="flex-1 py-3 rounded-xl bg-indigo-600 text-white text-xs font-bold flex justify-center gap-2"><RefreshCw v-if="topupSubmitting" class="w-4 h-4 animate-spin" />{{ topupSubmitting ? 'Memproses...' : 'Konfirmasi Top-Up' }}</button>
          </div>
        </div>

        <div v-else class="text-center space-y-4">
          <CheckCircle2 class="w-12 h-12 text-emerald-500 mx-auto" />
          <div><h3 class="font-black text-slate-900">Permintaan top-up berhasil diajukan</h3><p class="text-xs text-slate-600 mt-1">{{ formatRupiah(topupResult?.amount_minor) }} sedang menunggu persetujuan Back Office.</p></div>
          <div class="p-4 rounded-2xl bg-amber-50 text-amber-900 space-y-2 text-xs"><div class="flex justify-between"><span>Status</span><strong>MENUNGGU PERSETUJUAN</strong></div><div class="flex justify-between"><span>Referensi</span><strong class="font-mono">{{ topupResult?.request_reference }}</strong></div><div class="flex justify-between"><span>Pembayaran</span><strong>SIMULASI</strong></div><div class="flex justify-between border-t border-amber-200 pt-2"><span>Saldo saat ini</span><strong>{{ formatRupiah(topupResult?.balance_minor) }}</strong></div></div>
          <button type="button" @click="topupModalOpen = false" class="w-full py-3 rounded-xl bg-indigo-600 text-white text-xs font-bold">Selesai</button>
        </div>
      </div>
    </div>

    <!-- PLN Purchase Review Modal -->
    <div v-if="tokenPlnReviewModalOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
      <div class="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full space-y-6 shadow-2xl border border-slate-100 relative">
        <div class="flex items-center justify-between border-b border-slate-100 pb-4">
          <div class="flex items-center gap-2">
            <span class="px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 font-extrabold text-[10px] uppercase">
              MODE SIMULASI
            </span>
            <h3 class="font-bold text-sm text-slate-900">Review Pembelian Token PLN</h3>
          </div>
          <button @click="tokenPlnReviewModalOpen = false" class="text-slate-400 hover:text-slate-600">
            <X class="w-5 h-5" />
          </button>
        </div>

        <div class="space-y-3 text-xs">
          <div class="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-2">
            <div class="flex justify-between">
              <span class="text-slate-500">Nomor Meter / ID:</span>
              <span class="font-mono font-bold text-slate-900">{{ tokenPlnInquiry?.customer_number }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-slate-500">Nama Pelanggan:</span>
              <span class="font-bold text-slate-900">{{ tokenPlnInquiry?.customer_name }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-slate-500">Tarif / Daya:</span>
              <span class="font-bold text-slate-900">{{ tokenPlnInquiry?.tariff }} / {{ tokenPlnInquiry?.power_va }} VA</span>
            </div>
            <div class="flex justify-between border-t border-slate-200 pt-2">
              <span class="text-slate-500">Produk Token:</span>
              <span class="font-bold text-indigo-600">{{ tokenPlnSelectedSku?.name }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-slate-500">Harga Jual:</span>
              <span class="font-black text-slate-900">{{ formatRupiah(tokenPlnSelectedSku?.fallback_selling_price_minor) }}</span>
            </div>
          </div>

          <div class="p-3.5 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 text-[11px] leading-relaxed flex items-start gap-2.5">
            <Info class="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <span>Mode simulasi: Transaksi ini tidak akan memotong saldo dompet Anda.</span>
          </div>
        </div>

        <div class="flex items-center gap-3 pt-2">
          <button
            @click="tokenPlnReviewModalOpen = false"
            type="button"
            class="flex-1 py-3 rounded-2xl border border-slate-300 text-slate-700 font-bold text-xs hover:bg-slate-50 transition"
          >
            Kembali
          </button>
          <button
            @click="confirmTokenPlnPurchase"
            :disabled="tokenPlnIsSubmitting"
            type="button"
            class="flex-1 py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs shadow-md transition flex items-center justify-center gap-2 cursor-pointer"
          >
            <RefreshCw v-if="tokenPlnIsSubmitting" class="w-4 h-4 animate-spin" />
            <span>{{ tokenPlnIsSubmitting ? 'Memproses...' : 'Konfirmasi Simulasi' }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- PLN Token Result Modal -->
    <div v-if="tokenPlnResultModalOpen && tokenPlnPurchaseResult" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
      <div class="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full space-y-6 shadow-2xl border border-slate-100 relative">
        <div class="text-center space-y-2">
          <div class="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto shadow-xs">
            <CheckCircle2 class="w-7 h-7" />
          </div>
          <h3 class="text-lg font-black text-slate-900">SIMULASI BERHASIL</h3>
          <span class="inline-block px-3 py-0.5 rounded-full bg-amber-100 text-amber-900 text-[10px] font-extrabold uppercase">
            SIMULASI LOKAL
          </span>
        </div>

        <!-- 20-Digit Mock Token Card -->
        <div class="p-5 rounded-2xl bg-slate-900 text-white text-center space-y-2 shadow-md">
          <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">NOMOR TOKEN STROOM SIMULASI</span>
          <div class="font-mono text-lg sm:text-xl font-black text-amber-300 tracking-wider">
            {{ tokenPlnPurchaseResult.token }}
          </div>
          <button
            @click="copyPlnToken(tokenPlnPurchaseResult.token)"
            type="button"
            class="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-xs text-slate-200 transition font-medium"
          >
            <Copy class="w-3.5 h-3.5" />
            <span>{{ copiedToken ? 'Tersalin!' : 'Salin Token' }}</span>
          </button>
        </div>

        <div class="grid grid-cols-2 gap-3 text-xs bg-slate-50 p-4 rounded-2xl border border-slate-200">
          <div>
            <span class="text-[10px] text-slate-500 block">No. Transaksi</span>
            <span class="font-mono font-bold text-slate-900 truncate block">{{ tokenPlnPurchaseResult.transaction_reference }}</span>
          </div>
          <div>
            <span class="text-[10px] text-slate-500 block">No. Meter / ID</span>
            <span class="font-mono font-bold text-slate-900">{{ maskIdentifier(tokenPlnPurchaseResult.customer_number) }}</span>
          </div>
          <div>
            <span class="text-[10px] text-slate-500 block">Nama Pelanggan</span>
            <span class="font-bold text-slate-900">{{ tokenPlnPurchaseResult.customer_name }}</span>
          </div>
          <div>
            <span class="text-[10px] text-slate-500 block">Produk</span>
            <span class="font-bold text-slate-900">{{ tokenPlnSelectedSku?.name }}</span>
          </div>
          <div>
            <span class="text-[10px] text-slate-500 block">Harga Jual</span>
            <span class="font-bold text-slate-900">{{ formatRupiah(tokenPlnPurchaseResult.selling_price_minor) }}</span>
          </div>
          <div>
            <span class="text-[10px] text-slate-500 block">Waktu</span>
            <span class="font-mono text-[10px] text-slate-600 block">{{ tokenPlnPurchaseResult.completed_at || 'Baru saja' }}</span>
          </div>
        </div>

        <p class="text-[11px] text-slate-500 italic text-center">
          * Transaksi simulasi tidak memotong saldo dan tidak menghasilkan token PLN riil.
        </p>

        <button
          @click="resetTokenPlnFlow"
          type="button"
          class="w-full py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs shadow-md transition cursor-pointer"
        >
          Transaksi Baru
        </button>
      </div>
    </div>

    <!-- Transaction Detail Modal -->
    <div v-if="selectedDetailTx" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
      <div class="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full space-y-6 shadow-2xl border border-slate-100 relative">
        <div class="flex items-center justify-between border-b border-slate-100 pb-3">
          <h3 class="font-bold text-sm text-slate-900">Detail Transaksi</h3>
          <button @click="selectedDetailTx = null" class="text-slate-400 hover:text-slate-600">
            <X class="w-5 h-5" />
          </button>
        </div>

        <div v-if="selectedDetailTx.result_json?.token" class="p-4 rounded-2xl bg-slate-900 text-white text-center space-y-2">
          <span class="text-[10px] text-slate-400 font-bold block uppercase">TOKEN SIMULASI</span>
          <div class="font-mono text-lg font-black text-amber-300 tracking-wider">
            {{ selectedDetailTx.result_json.token }}
          </div>
        </div>

        <div v-else-if="selectedDetailTx.result_json?.serial_number" class="p-4 rounded-2xl bg-slate-900 text-white text-center space-y-2">
          <span class="text-[10px] text-slate-400 font-bold block uppercase">NOMOR SERIAL SIMULASI</span>
          <div class="font-mono text-sm font-black text-amber-300 tracking-wider">
            {{ selectedDetailTx.result_json.serial_number }}
          </div>
        </div>

        <div v-else-if="selectedDetailTx.result_json?.payment_reference" class="p-4 rounded-2xl bg-slate-900 text-white text-center space-y-2">
          <span class="text-[10px] text-slate-400 font-bold block uppercase">REFERENSI BAYAR SIMULASI</span>
          <div class="font-mono text-sm font-black text-amber-300 tracking-wider">
            {{ selectedDetailTx.result_json.payment_reference }}
          </div>
        </div>

        <div class="space-y-2 text-xs bg-slate-50 p-4 rounded-2xl border border-slate-200">
          <div class="flex justify-between">
            <span class="text-slate-500">Ref Transaksi:</span>
            <span class="font-mono font-bold">{{ selectedDetailTx.reference }}</span>
          </div>
          <div v-if="!['wallet', 'topup_request'].includes(selectedDetailTx.kind)" class="flex justify-between">
            <span class="text-slate-500">Nomor / ID Tujuan:</span>
            <span class="font-mono font-bold">{{ selectedDetailTx.beneficiary_identifier }}</span>
          </div>
          <div v-if="selectedDetailTx.customer_snapshot_json?.billing_period" class="flex justify-between">
            <span class="text-slate-500">Periode Tagihan:</span>
            <span class="font-bold text-indigo-600">{{ selectedDetailTx.customer_snapshot_json.billing_period }}</span>
          </div>
          <div v-if="!['wallet', 'topup_request'].includes(selectedDetailTx.kind)" class="flex justify-between">
            <span class="text-slate-500">SKU / Produk:</span>
            <span class="font-bold">{{ formatTxTitle(selectedDetailTx) }}</span>
          </div>
          <div v-if="!['wallet', 'topup_request'].includes(selectedDetailTx.kind)" class="flex justify-between">
            <span class="text-slate-500">Total Harga:</span>
            <span class="font-black text-slate-900">{{ formatRupiah(selectedDetailTx.selling_price_minor) }}</span>
          </div>
          <template v-else-if="selectedDetailTx.kind === 'wallet'">
            <div class="flex justify-between"><span class="text-slate-500">Jenis:</span><strong>Tambah Saldo</strong></div>
            <div class="flex justify-between"><span class="text-slate-500">Nominal:</span><strong class="text-emerald-600">+{{ formatRupiah(selectedDetailTx.amount_minor) }}</strong></div>
            <div class="flex justify-between"><span class="text-slate-500">Saldo Sebelum:</span><strong>{{ formatRupiah(selectedDetailTx.balance_before_minor) }}</strong></div>
            <div class="flex justify-between"><span class="text-slate-500">Saldo Sesudah:</span><strong>{{ formatRupiah(selectedDetailTx.balance_after_minor) }}</strong></div>
            <div class="flex justify-between"><span class="text-slate-500">Pembayaran:</span><strong class="uppercase">{{ selectedDetailTx.payment_method }}</strong></div>
          </template>
          <template v-else>
            <div class="flex justify-between"><span class="text-slate-500">Jenis:</span><strong>Permintaan Tambah Saldo</strong></div>
            <div class="flex justify-between"><span class="text-slate-500">Nominal:</span><strong>{{ formatRupiah(selectedDetailTx.requested_amount_minor) }}</strong></div>
            <div class="flex justify-between"><span class="text-slate-500">Pembayaran:</span><strong class="uppercase">{{ selectedDetailTx.payment_method }}</strong></div>
            <div v-if="selectedDetailTx.review_notes" class="flex justify-between gap-4"><span class="text-slate-500">Catatan Review:</span><strong class="text-right">{{ selectedDetailTx.review_notes }}</strong></div>
          </template>
          <div class="flex justify-between">
            <span class="text-slate-500">Status:</span>
            <span class="font-bold uppercase">{{ topupStatusLabel(selectedDetailTx.status) }}</span>
          </div>
          <div class="flex justify-between border-t border-slate-200 pt-2">
            <span class="text-slate-500">Waktu:</span>
            <span>{{ new Date(selectedDetailTx.created_at).toLocaleString('id-ID') }}</span>
          </div>
        </div>

        <button @click="selectedDetailTx = null" type="button" class="w-full py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs">
          Tutup
        </button>
      </div>
    </div>

    <!-- Registered User & Guest Transactions History Section -->
    <section class="space-y-4">
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-lg font-bold text-slate-900">Riwayat Transaksi {{ isGuest ? '(Mode Tamu)' : '' }}</h2>
          <p class="text-xs text-slate-500 mt-0.5">Catatan aktivitas transaksi digital Anda</p>
        </div>
        <Link :href="route('transactions.index')" class="text-xs font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
          Lihat semua <ArrowRight class="w-3.5 h-3.5" />
        </Link>
      </div>

      <TransactionHistoryList :transactions="transactions" />
      <div v-if="false" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div
          v-for="tx in transactions"
          :key="tx.id"
          @click="viewTransactionDetail(tx)"
          class="p-4 rounded-2xl border border-slate-200 bg-white hover:border-indigo-400 transition cursor-pointer space-y-3 shadow-2xs"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-1.5 truncate">
              <Wallet v-if="tx.kind === 'wallet' || tx.kind === 'topup_request'" class="w-4 h-4 text-emerald-500 shrink-0" />
              <Zap v-else-if="tx.sku?.product?.code === 'PLN_TOKEN' || tx.sku?.product?.code === 'PLN_POSTPAID'" class="w-4 h-4 text-amber-500 shrink-0" />
              <Smartphone v-else-if="tx.sku?.product?.product_category_id === 1" class="w-4 h-4 text-indigo-500 shrink-0" />
              <Wifi v-else-if="tx.sku?.product?.product_category_id === 2" class="w-4 h-4 text-emerald-500 shrink-0" />
              <WalletCards v-else-if="tx.sku?.product?.product_category_id === 4" class="w-4 h-4 text-blue-500 shrink-0" />
              <Receipt v-else class="w-4 h-4 text-slate-500 shrink-0" />
              <span class="font-bold text-xs text-slate-900 truncate">
                {{ formatTxTitle(tx) }}
              </span>
            </div>
            <span v-if="tx.is_mock || tx.is_simulation" class="text-[9px] font-extrabold bg-amber-100 text-amber-900 px-2 py-0.5 rounded-full uppercase shrink-0">
              SIMULASI
            </span>
          </div>

          <div class="text-xs space-y-1">
            <div v-if="!['wallet', 'topup_request'].includes(tx.kind)" class="flex justify-between">
              <span class="text-slate-500 text-[11px]">Nomor:</span>
              <span class="font-mono font-bold text-slate-800">{{ maskIdentifier(tx.beneficiary_identifier) }}</span>
            </div>
            <div v-if="tx.customer_snapshot_json?.billing_period" class="flex justify-between">
              <span class="text-slate-500 text-[11px]">Periode:</span>
              <span class="font-bold text-indigo-600">{{ tx.customer_snapshot_json.billing_period }}</span>
            </div>
            <div v-if="!['wallet', 'topup_request'].includes(tx.kind)" class="flex justify-between">
              <span class="text-slate-500 text-[11px]">Total:</span>
              <span class="font-black text-indigo-600">{{ formatRupiah(tx.selling_price_minor) }}</span>
            </div>
            <div v-else class="flex justify-between">
              <span class="text-slate-500 text-[11px]">Nominal:</span>
              <span class="font-black" :class="tx.status === 'approved' || tx.status === 'success' ? 'text-emerald-600' : 'text-slate-800'">{{ tx.status === 'approved' || tx.status === 'success' ? '+' : '' }}{{ formatRupiah(tx.kind === 'topup_request' ? tx.requested_amount_minor : tx.amount_minor) }}</span>
            </div>
          </div>

          <div class="flex items-center justify-between pt-2 border-t border-slate-100 text-[10px] text-slate-400 font-medium">
            <span>{{ new Date(tx.created_at).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }) }}</span>
            <span :class="topupStatusClass(tx.status)" class="font-bold uppercase px-2 py-0.5 rounded-full border">
              {{ topupStatusLabel(tx.status) }}
            </span>
          </div>
        </div>
      </div>

      <EmptyState v-if="false"
        title="Belum Ada Transaksi"
        description="Riwayat transaksi simulasi PPOB Anda akan muncul di sini."
      />
    </section>
  </div>
</template>
